import { App as CapacitorApp } from '@capacitor/app';

import { getCurrentSession } from '../../../common/api/api-helpers';
import { IS_DEMO_LOGIN } from '../../../common/constants';
import { ENV_TYPE_SHORTHAND } from '../../../env-type';
import { REGION_SHORTHAND } from '../../../region-type';
import demoLiveData from '../../demo-login/stubs/sense-live-data.json';
import { LiveDataMessage, LiveDeviceStatus, LiveSenseData } from './types';

const WEBSOCKET_ENDPOINT = import.meta.env[
  `VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_WEBSOCKET_API_URL`
] as string;

const MAX_RETRIES = 2;
const RETRY_TIMEOUT_MS = 15_000;

export const liveDataWebhookQueryFn = ({ siteId: _, trigger: __ }: { siteId: number; trigger: number }) => {
  return {
    data: IS_DEMO_LOGIN
      ? {
          ...(demoLiveData.payload as LiveSenseData),
          isLoaded: true,
          isError: false,
          error: null,
        }
      : {
          clipsal_solar_id: 0,
          ac_load_net: 0,
          consumption: 0,
          devices: [],
          switches: [],
          epoch: 0,
          solar: 0,
          hybrid_inverter: 0,
          isLoaded: false,
          isError: false,
          error: null,
        },
  };
};

type LiveDataDraft = LiveSenseData & {
  isLoaded: boolean;
  isError: boolean;
  error: unknown;
};

export const onCacheEntryAddedHandler = async (
  arg: { siteId: number; trigger: number },
  api: {
    updateCachedData: (recipe: (draft: LiveDataDraft) => void) => void;
    cacheDataLoaded: Promise<void>;
    cacheEntryRemoved: Promise<void>;
  }
) => {
  const { siteId, trigger: _ } = arg;
  const { updateCachedData, cacheDataLoaded, cacheEntryRemoved } = api;

  await cacheDataLoaded;

  if (IS_DEMO_LOGIN) {
    updateCachedData(() => ({
      ...(demoLiveData.payload as LiveSenseData),
      isLoaded: true,
      isError: false,
      error: null,
    }));
    return;
  }

  // Background/foreground checks help manage WS logic between app states.
  let { isActive } = await CapacitorApp.getState();
  const appStateChangeListener = await CapacitorApp.addListener('appStateChange', ({ isActive: active }) => {
    isActive = active;
    if (isActive) {
      handleOnline(); // We can re-use the same behaviour as coming back online here
    } else {
      clearTimeout(wsTimeout); // Pause retries while backgrounded
    }
  });

  let socket: WebSocket | null = null;
  let wsTimeout: ReturnType<typeof setTimeout>;
  let retryCount = 0;
  const { token } = await getCurrentSession();

  const resetWebsocketTimer = () => {
    clearTimeout(wsTimeout);
    if (!isActive) return;
    wsTimeout = setTimeout(() => {
      if (!isActive) return;
      retryCount >= MAX_RETRIES ? handleError('WEBSOCKET_ERROR') : reconnect();
    }, RETRY_TIMEOUT_MS);
  };

  const handleError = (error: string) => {
    if (!isActive) return;
    cleanup();
    updateCachedData((draft) => {
      draft.isLoaded = true;
      draft.isError = true;
      draft.error = error;
    });
  };

  const cleanup = () => {
    return new Promise<void>((resolve) => {
      // We clear the timeout to prevent it from firing after the socket is closed
      clearTimeout(wsTimeout);
      if (!socket) return resolve();
      try {
        send({ action: 'unsubscribeSiteLiveData', clipsal_solar_id: siteId });
        socket.close();
      } catch (e) {
        console.warn('[WS] Unsubscribe error', e);
      }
      socket = null;
      resolve();
    });
  };

  const reconnect = () => {
    if (retryCount >= MAX_RETRIES) {
      // Reconnect could fire on lingering WS connections after exceeded retry counts, this ensures proper clean up
      handleError('WEBSOCKET_ERROR');
      return;
    }

    retryCount += 1;
    updateCachedData((draft) => {
      draft.isLoaded = false;
      draft.isError = false;
      draft.error = null;
    });
    console.info(`[WS] Reconnecting... attempt ${retryCount}`);
    connect();
  };

  /* istanbul ignore next -- @preserve */
  const handleWebSocketMessage = (event: MessageEvent) => {
    try {
      const data: LiveDataMessage = JSON.parse(event.data);
      switch (data?.type) {
        case 'realtime_update':
          retryCount = 0;

          updateCachedData((draft) => {
            Object.assign(draft, {
              ...data.payload,
              isLoaded: true,
              isError: false,
              error: null,
            });
          });
          break;
        case 'reconnect':
          send({ action: 'subscribeSiteLiveData', clipsal_solar_id: siteId });
          break;
        case 'close':
        case 'error':
          console.warn('[WS] Server closed or errored, retrying...');
          handleError('WEBSOCKET_ERROR');
          break;
        case 'open':
        case 'info':
          // No-op
          break;
        case 'site_device_status':
          console.info('[WS] Device status update:', data.payload);
          const deviceStatus = data.payload as LiveDeviceStatus;
          if (deviceStatus.operating_state === 'OFFLINE' || deviceStatus.operating_state === 'UNREACHABLE') {
            console.warn('[WS] Aborting connect: SEM is offline/unreachable');
            handleError('WEBSOCKET_ERROR');
            retryCount = MAX_RETRIES;
          }
          break;
        default:
          console.warn('[WS] Unknown message:', data);
      }
      resetWebsocketTimer();
    } catch (err) {
      console.error('[WS] JSON parse or handle error', err);
      handleError('WEBSOCKET_ERROR');
    }
  };

  const connect = () => {
    resetWebsocketTimer();
    if (socket && socket.readyState !== WebSocket.CLOSED && socket.readyState !== WebSocket.CLOSING) {
      console.warn('[WS] Aborting connect: socket still active');
    } else {
      socket = new WebSocket(`${WEBSOCKET_ENDPOINT}/?token=${token}`);

      socket.onopen = () => send({ action: 'subscribeSiteLiveData', clipsal_solar_id: siteId });

      socket.onmessage = handleWebSocketMessage;

      socket.onerror = socket.onclose = reconnect;
    }
  };

  const send = (msg: object) => {
    // Ensure WS messages are sent only when the socket is open
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(msg));
    } else {
      console.warn('[WS] Tried to send on socket not open, readyState:', socket?.readyState);
    }
  };

  /* istanbul ignore next -- @preserve */
  const handleOffline = () => {
    handleError('WEBSOCKET_ERROR');
  };
  /* istanbul ignore next -- @preserve */
  const handleOnline = () => {
    retryCount = 0;
    reconnect();
  };

  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  connect();

  await cacheEntryRemoved;
  await cleanup();

  appStateChangeListener.remove();
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('online', handleOnline);
};
